import { storageMng } from "./StorageMng";

// Guards against reload loops: only try the recovery reload once per tab session.
const RECOVERY_FLAG = "mtu.authRecoveryAttempted";

/**
 * Call from a route guard once it has decided the user looks "not authorized".
 * react-secure-storage derives its AES key from a browser fingerprint (which
 * includes a canvas print) that can differ between page loads, causing an
 * otherwise-valid session to intermittently fail to decrypt on a given load.
 * A plain, unencrypted "session active" marker (set on login, cleared on
 * logout — see storageMng.storSessionActive) lets us tell that case apart
 * from a real logout: if the marker says a session should exist, one fresh
 * reload is worth trying — a new page load means a new fingerprint/decrypt
 * attempt — before bouncing the user to the not-authorized page.
 *
 * Returns true if a recovery reload was triggered (the caller should render
 * nothing and stop). Returns false if recovery was already attempted this
 * tab session, or there's no session to recover — i.e. it really is
 * unauthorized and the caller should proceed with its normal redirect.
 */
export const tryRecoverAuth = (): boolean => {
  const alreadyTried = sessionStorage.getItem(RECOVERY_FLAG) === "1";
  if (alreadyTried || !storageMng.isSessionActive()) return false;

  sessionStorage.setItem(RECOVERY_FLAG, "1");
  window.location.reload();
  return true;
};

/** Call once a guard confirms the user IS authorized, so the next transient
 * failure (if any) gets its own recovery attempt instead of being silently
 * skipped because of a stale flag from an earlier, unrelated glitch. */
export const clearAuthRecoveryFlag = () => {
  sessionStorage.removeItem(RECOVERY_FLAG);
};
